import React, { useEffect, useState } from 'react';
import { Button } from '@duxton-web/ui';
import * as lscache from 'lscache';

import {
  CONCEDO_CODE_VERIFIER,
  CONCEDO_REDIRECT_URI,
  CONCEDO_STATE,
  CONCEDO_CLIENT_ID as LSCACHE_CONCEDO_CLIENT_ID,
  REDIRECT_USER_REQUEST_URI,
} from '@/common/const/LSCache';
import {
  CONCEDO_CHOPE_CLIENT_ID,
  CONCEDO_CLIENT_ID,
  CONCEDO_IDC_URL,
  CONCEDO_MEXLITE_CLIENT_ID,
  CONCEDO_MOVEIT_CLIENT_ID,
  CONCEDO_OVO_CLIENT_ID,
} from '@/common/environment';
import { CHOPE, EXPERIMENTS, MEXLITE, MOVEIT, OVO } from '@/common/Tenants';
import {
  codeChallengeFromVerifier,
  randomHex,
} from '@/utils/concedoAuthHelper';

export interface ConcedoLoginButtonProps {
  tenant: string;
}

export const ConcedoLoginButton = (
  props: ConcedoLoginButtonProps,
): JSX.Element => {
  const [loading, isLoading] = useState(false);
  const urlParams = new URLSearchParams(window.location.search);
  const code = urlParams.get('code');
  const state = urlParams.get('state');
  const shouldRelogin = urlParams.get('shouldRelogin');

  const handleLogin = async (): Promise<void> => {
    isLoading(true);
    const state = randomHex();
    lscache.set(CONCEDO_STATE, state);

    // Generate a code challenge from a code verifier. We store the code verifier for later retrieval in the callback.
    const codeVerifier = randomHex();
    lscache.set(CONCEDO_CODE_VERIFIER, codeVerifier);
    const codeChallenge = await codeChallengeFromVerifier(codeVerifier);

    let clientID = CONCEDO_CLIENT_ID;

    switch (props.tenant) {
      case EXPERIMENTS:
        clientID = CONCEDO_CLIENT_ID;
        break;
      case MOVEIT:
        clientID = CONCEDO_MOVEIT_CLIENT_ID;
        break;
      case CHOPE:
        clientID = CONCEDO_CHOPE_CLIENT_ID;
        break;
      case OVO:
        clientID = CONCEDO_OVO_CLIENT_ID;
        break;
      case MEXLITE:
        clientID = CONCEDO_MEXLITE_CLIENT_ID;
        break;
      default:
        clientID = CONCEDO_CLIENT_ID;
    }
    // Set ClientID for later retrieval to put into /login request header
    lscache.set(LSCACHE_CONCEDO_CLIENT_ID, clientID);
    const redirectURI = window.location.origin;
    const pathname = window.location.pathname;
    const isLoginPage = ['', '/', '/login'].includes(pathname);

    if (!isLoginPage) {
      lscache.set(REDIRECT_USER_REQUEST_URI, window.location.href);
    } else {
      lscache.set(REDIRECT_USER_REQUEST_URI, '');
    }

    lscache.set(CONCEDO_REDIRECT_URI, redirectURI);
    // Redirect the browser to Concedo IDC's auth endpoint.

    const authRedirectURL = `${CONCEDO_IDC_URL}/auth?${new URLSearchParams({
      client_id: clientID,
      scope: 'openid email concedo',
      state: state,
      response_type: 'code',
      redirect_uri: redirectURI,
      code_challenge_method: 'S256',
      code_challenge: codeChallenge,
    })}`;

    // Add a slight delay for React to update the loading state before redirecting
    setTimeout(() => {
      window.location.href = authRedirectURL;
    }, 300);
  };

  useEffect(() => {
    if (!code && !state && !shouldRelogin) {
      handleLogin();
    }
  }, []);

  return (
    <Button onClick={() => handleLogin()} size={'small'} loading={loading}>
      Login with Concedo
    </Button>
  );
};
